import { Box, CircularProgress } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { ApiError, DetailView } from '@pro4-erp/core-web';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { AuditorCompetenceDetail } from '@/common';
import { getAuditorCompetenceById } from '../../api/getAuditorCompetenceById';
import { updateAuditorCompetence } from '../../api/updateAuditorCompetence';
import Form from '../../components/Form';
import Files from './Files';
import { getMessageFromError } from '@/core';
import { useDetailPageStore } from './useDetailPageStore';

const DetailPage = () => {
  const { id } = useParams<{ id: string }>() as { id: string };

  const detailPageStore = useDetailPageStore();

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const auditorCompetenceQuery = useQuery<AuditorCompetenceDetail, ApiError>(
    ['auditor-competences-id', id],
    () => getAuditorCompetenceById(id),
    { refetchOnWindowFocus: false }
  );

  useEffect(() => {
    return () => {
      detailPageStore.reset();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!auditorCompetenceQuery.data) {
      return;
    }

    detailPageStore.setForm({
      name: auditorCompetenceQuery.data.name || '',
      note: auditorCompetenceQuery.data.note || '',
      validFrom: auditorCompetenceQuery.data.validFrom || '',
      validTo: auditorCompetenceQuery.data.validTo || '',
      confirmDat: auditorCompetenceQuery.data.confirmDat || '',
      statusNote: auditorCompetenceQuery.data.statusNote || '',
      CTStartDate: auditorCompetenceQuery.data.CTStartDate || '',
      CTEndDate: auditorCompetenceQuery.data.CTEndDate || '',
      CTEducationLevel: auditorCompetenceQuery.data.CTEducationLevel || '',
      CTInstitutionName: auditorCompetenceQuery.data.CTInstitutionName || '',
      CTAttainedTitle: auditorCompetenceQuery.data.CTAttainedTitle || '',
      CTCourseOrSeminarType: auditorCompetenceQuery.data.CTCourseOrSeminarType || '',
      CTOrganizer: auditorCompetenceQuery.data.CTOrganizer || '',
      CTConfirmation: auditorCompetenceQuery.data.CTConfirmation || '',
      CTAttendType: auditorCompetenceQuery.data.CTAttendType || '',
      CTLanguageName: auditorCompetenceQuery.data.CTLanguageName || '',
      CTLanguageListeningComprehension:
        auditorCompetenceQuery.data.CTLanguageListeningComprehension || 0,
      CTLanguageReadingComprehension:
        auditorCompetenceQuery.data.CTLanguageReadingComprehension || 0,
      CTLanguageSpeakingLevel: auditorCompetenceQuery.data.CTLanguageSpeakingLevel || 0,
      CTLanguageWritingLevel: auditorCompetenceQuery.data.CTLanguageWritingLevel || 0,
      CTOrganizationTitle: auditorCompetenceQuery.data.CTOrganizationTitle || '',
      CTOrganizationLocation: auditorCompetenceQuery.data.CTOrganizationLocation || '',
      CTOrganizationWebsite: auditorCompetenceQuery.data.CTOrganizationWebsite || '',
      CTTasks: auditorCompetenceQuery.data.CTTasks || '',
      CTStandardActivityClassification:
        auditorCompetenceQuery.data.CTStandardActivityClassification || '',
      CTPosition: auditorCompetenceQuery.data.CTPosition || '',
      CTProjectTitle: auditorCompetenceQuery.data.CTProjectTitle || '',
      CTRole: auditorCompetenceQuery.data.CTRole || '',
      CTCountry: auditorCompetenceQuery.data.CTCountry || '',
      compType: auditorCompetenceQuery.data.type || null,
    });
    detailPageStore.setIsLoading(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditorCompetenceQuery.data]);

  const { mutate } = useMutation({
    mutationFn: updateAuditorCompetence,
    onSuccess: () => {
      enqueueSnackbar(
        t('Successfully saved changes.', { defaultValue: 'Successfully saved changes.' }),
        { variant: 'success' }
      );
      queryClient.invalidateQueries(['auditor-competences-id', id]);
    },
    onError: (error: Error) => {
      enqueueSnackbar(getMessageFromError(error), { variant: 'error' });
    },
  });

  if (auditorCompetenceQuery.isLoading || detailPageStore.isLoading) {
    return (
      <Box>
        <Box
          sx={{
            width: '100vw',
            height: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress size={100} />
        </Box>
      </Box>
    );
  }

  if (auditorCompetenceQuery.isError) {
    return <p>{auditorCompetenceQuery.error.message}</p>;
  }

  return (
    <Box sx={{ mt: 1 }}>
      <DetailView
        childrenSx={{ padding: '2rem' }}
        title={<DetailView.Title>{auditorCompetenceQuery.data.name || 'N/A'}</DetailView.Title>}
        onGoBack={() => navigate('/auditor-competences')}
        tabs={[
          { id: 'details', title: t('Details', { defaultValue: 'Details' }) },
          { id: 'files', title: t('Files', { defaultValue: 'Files' }) },
        ]}
        defaultTab="details"
      >
        {({ TabContent }) => (
          <>
            <TabContent tab="details">
              <Form
                onSubmit={(data) => {
                  mutate({
                    id: auditorCompetenceQuery.data.id,
                    data: {
                      compTypeId: data.compType?.id,
                      ...data,
                    },
                  });
                  navigate('/auditor-competences');
                }}
                initialState={detailPageStore.form}
                onUnmount={(form) => detailPageStore.setForm({ ...form })}
                isFormEditable={auditorCompetenceQuery.data.isEditableOnPortal}
              />
            </TabContent>
            <TabContent tab="files">
              <Files auditorCompetenceDetail={auditorCompetenceQuery.data} />
            </TabContent>
          </>
        )}
      </DetailView>
    </Box>
  );
};

export default DetailPage;
